import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
	MaterialReactTable,
	MRT_DensityState,
	MRT_PaginationState,
	MRT_Row, MRT_RowSelectionState,
	MRT_SortingState,
	MRT_VisibilityState,
} from 'material-react-table';
import {
	ColumnSizing,
	setDensity,
	setResize,
	TablesData,
	TablesNames,
	useTablesContext,
	setVisibilityState,
} from '../../store/tables/tables-context';
import { useDebounce } from 'use-debounce';
import { checkRoles } from '../../utils/roles-helper';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { ActionName, ILink } from '../../interfaces/links';
import { getLinkField } from '../../utils/linksHelper';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useMerchants } from '../../store/merchnts/merchants-context';

import './MaterialReactTable.scss';

type Props = {
	name: TablesNames;
	sorting?: MRT_SortingState;
	pageNumber?: number;
	pageSize?: number;
	setSorting?: (sorting: MRT_SortingState) => void;
	setPagination?: (pagination: MRT_PaginationState) => void;
	data: any;
	isLoading: boolean;
	totalItemCount?: number;
	toolbarCustomAction?: JSX.Element;
	enableRowActions?: boolean;
	rowActions?: (value: MRT_Row) => JSX.Element;
	rowActionMenuItems?: (value: MRT_Row, closeMenu?: any) => ReactNode[];
	actionColumnsSize?: number;
	actionColumnAlign?: 'left' | 'right';
	renderDetailPanel?: (row: MRT_Row) => React.ReactElement;
	rowSelection?: MRT_RowSelectionState;
	setRowSelection?: (value: MRT_RowSelectionState) => void
	enableRowSelection?: boolean;
	enableRowSelectionCondition?: any;
	enableSelectAll?: boolean;
	enableMultiRowSelection?: boolean;
	enableRowClicking?: boolean;
	positionToolbarAlertBanner?: string;
	positionActionsColumn?: 'first' | 'last';
	enableDensityToggle?: boolean;
	enableHiding?: boolean;
	enableFullScreenToggle?: boolean;
	enableColumnActions?: boolean;
	enableColumnResizing?: boolean;
	enableTopToolbar?: boolean;
	enableBottomToolbar?: boolean;
	paginationLinks?: ILink[];
	queryParamsListener?: (params: any) => void;
	isRefreshed?: boolean;
}

const pagesWithLinks = ['/Orders', '/orders'];

export const Table: FC<Props> = (props) => {
	const {
		name,
		sorting,
		setSorting,
		setPagination,
		data,
		isLoading,
		totalItemCount,
		toolbarCustomAction,
		rowActions,
		rowActionMenuItems,
		actionColumnsSize,
		renderDetailPanel,
		rowSelection,
		setRowSelection,
		enableRowActions = false,
		enableRowSelection,
		enableRowSelectionCondition,
		enableSelectAll,
		enableMultiRowSelection,
		positionToolbarAlertBanner,
		enableRowClicking,
		positionActionsColumn='first',
		enableDensityToggle = true,
		enableHiding = true,
		enableFullScreenToggle = true,
		enableColumnActions = true,
		enableColumnResizing = true,
		enableTopToolbar = true,
		enableBottomToolbar = true,
		paginationLinks,
		queryParamsListener,
		isRefreshed,
		actionColumnAlign = 'center'
	} = props;

	const navigate = useNavigate();
	const { pathname, search, state } = useLocation();
	const isMounted = useRef(false);
	const isHandlingPageSizeChange = useRef(false);
	const isHandlingPageNumberSync = useRef(false);

	const pageNumberFromQuery = new URLSearchParams(search).get('pageNumber');
	const pageSizeFromQuery = new URLSearchParams(search).get('pageSize');

	const { merchantState: { chosenMerchant }} = useMerchants();

	const { tablesContextState, dispatch: tableDispatch } = useTablesContext();
	const { columnSizing, density, visibilityState, data: columnData, showsFor } = tablesContextState[name];
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const getVisibilityState = (): MRT_VisibilityState => {
		const savedOrderVisibilityState = JSON.parse(localStorage.getItem('savedOrderVisibilityState'));
		return savedOrderVisibilityState ? savedOrderVisibilityState : visibilityState;
	};
	const [disputesTableDensity, setComponentDensity] = useState<MRT_DensityState>(density);
	const [componentColumnVisibilityState, setComponentColumnVisibilityState] = useState<MRT_VisibilityState>(getVisibilityState());
	const [componentSorting, setComponentSorting] = useState<MRT_SortingState>(sorting);
	// const [componentSorting, setComponentSorting] = useState<MRT_SortingState>(getInitialSorting);
	const [componentPagination, setComponentPagination] = useState<MRT_PaginationState>({
		pageIndex: Number(pageNumberFromQuery) ? Number(pageNumberFromQuery) -1 : 0,
		pageSize: Number(pageSizeFromQuery) ? Number(pageSizeFromQuery) : 10,
	});
	const [disputesTableColumnSizing, setColumnsSize] = useState<ColumnSizing>(columnSizing);
	const [columnsSizeValue] = useDebounce(disputesTableColumnSizing, 1000);

	const pageFirst = getLinkField(paginationLinks, ActionName.GetFirst, 'href');
	const pageLast = getLinkField(paginationLinks, ActionName.GetLast, 'href');
	const pageNext = getLinkField(paginationLinks, ActionName.GetNext, 'href');
	const pagePrev = getLinkField(paginationLinks, ActionName.GetPrev, 'href');

	const isPageWithLinks = pagesWithLinks.includes(pathname.split('?')[0]);

	useEffect(() => {
		// if (!search && !pagesWithLinks.includes(pathname) && !initialSearchParams ) {
		// 	return;
		// } else {
		// 	if (
		// 		(queryParamsListener && search ||
		// 		queryParamsListener && initialSearchParams)
		// 	) {
		// 		queryParamsListener(search || `?${makeSearchParams(initialSearchParams)}`);
		// 	}
		// }
		if (queryParamsListener) {
			const objectOfFiltersFromUrl = parseSearchParams(search) || {};

			queryParamsListener(objectOfFiltersFromUrl);
		}

	}, [search]);

	useEffect(() => {
		if (setSorting) {
			setSorting(componentSorting);
		}
	}, [componentSorting]);

	useEffect(() => {
		if (!isMounted.current) return;

		if (setPagination) {
			setPagination(componentPagination);
		}

		const params = parseSearchParams(search) || {};

		if ((pageSizeFromQuery && componentPagination.pageSize !== Number(pageSizeFromQuery)) || (!pageSizeFromQuery && componentPagination?.pageSize !== 10)) {
			const newParams = {
				...params,
				pageSize: componentPagination.pageSize,
				pageNumber: 1,
				mid: chosenMerchant
			};

			// Set flag to indicate we're handling a pageSize change
			isHandlingPageSizeChange.current = true;

			setComponentPagination(prev => ({
				...prev,
				pageIndex: 0,
			}));

			return navigate(`${pathname}?${makeSearchParams(newParams)}`, { state: { ...state }});

		}

		// If we're currently handling a pageSize change, skip the rest of the logic
		if (isHandlingPageSizeChange.current) {
			isHandlingPageSizeChange.current = false;
			return;
		}

		// If we're currently handling a pageNumber sync, skip the rest of the logic
		if (isHandlingPageNumberSync.current) {
			isHandlingPageNumberSync.current = false;
			return;
		}

		if (isRefreshed) return;

		// Always update pageSize and pageNumber in params
		params.pageNumber = componentPagination.pageIndex + 1;
		params.pageSize = componentPagination.pageSize;
		params.mid = chosenMerchant;

		// If we have pagination links, use them
		if (componentPagination.pageIndex === 0 && pageFirst) {
			navigate(pageFirst, { state: { ...state }});
		} else if (componentPagination.pageIndex === Math.floor(totalItemCount/componentPagination.pageSize) && pageLast) {
			navigate(pageLast, { state: { ...state }});
		} else if ((+pageNumberFromQuery > componentPagination.pageIndex + 1) && pagePrev) {
			navigate(pagePrev, { state: { ...state }});
		} else if (pageNext) {
			navigate(pageNext, { state: { ...state }});
		} else {
			// If we don't have pagination links, construct the URL manually
			if (Object.keys(params).length) {
				navigate(`${pathname}?${makeSearchParams(params)}`, { state: { ...state }});
			}
		}
	}, [componentPagination]);

	useEffect(() => {
		if (isRefreshed) {
			setComponentPagination(prev => ({
				...prev,
				pageIndex: Number(pageNumberFromQuery) - 1,
			}));
		}
	}, [pageNumberFromQuery]);

	// Synchronize URL pageNumber with componentPagination.pageIndex
	useEffect(() => {
		if (!isMounted.current) return;

		const urlPageNumber = Number(pageNumberFromQuery) || 1;
		const expectedPageIndex = urlPageNumber - 1;

		// Only update if there's a mismatch between URL pageNumber and component pageIndex
		if (componentPagination.pageIndex !== expectedPageIndex) {
			// Set flag to indicate we're handling a pageNumber sync
			isHandlingPageNumberSync.current = true;

			setComponentPagination(prev => ({
				...prev,
				pageIndex: expectedPageIndex,
			}));
		}
	}, [pageNumberFromQuery]);

	useEffect(() => {
		setResize(tableDispatch, name, columnsSizeValue);
	}, [columnsSizeValue]);

	useEffect(() => {
		setDensity(tableDispatch, name, disputesTableDensity);
	}, [disputesTableDensity]);

	useEffect(() => {
		if(Object.keys(componentColumnVisibilityState).length && pathname === '/orders') {
			localStorage.setItem('savedOrderVisibilityState', JSON.stringify(componentColumnVisibilityState));
		}
		setVisibilityState(tableDispatch, name, componentColumnVisibilityState);
	}, [componentColumnVisibilityState]);

	useEffect(() => {
		if (!isMounted.current) {
			// first mount
			isMounted.current = true;
		}
	}, []);

	const columns = useMemo<TablesData>(
		() => columnData,
		[columnData]
	);

	const getColumnData = (): TablesData => {
		if(!showsFor) return columns;
		//@ts-ignore
		return columns?.filter(col => {
			if (!showsFor[col.accessorKey]) {
				return true;
			} else {
				return checkRoles(authUser, showsFor[col.accessorKey]);
			}
		});
	};

	const options: any = {
		columns: getColumnData() as any,
		data: data,
		enableTopToolbar,
		enableBottomToolbar,
		enableColumnResizing,
		enableColumnFilters: false,
		enableExpandAll: false,
		enableDensityToggle,
		enableHiding,
		enableFullScreenToggle,
		enableColumnActions,
		enablePagination: Boolean(totalItemCount),
		manualSorting: true,
		manualFiltering: true,
		manualPagination: true,
		onSortingChange: setComponentSorting,
		onPaginationChange: (args): any => {
			if (isMounted.current) {
				setComponentPagination(args);
			}
		},
		muiTablePaginationProps: {
			ActionsComponent: (subProps) => (
				<TablePaginationActions
					{...subProps}
					slotProps={isPageWithLinks ? { lastButton: { disabled: !pageLast }} : {}}
				/>
			),
		},
		onColumnSizingChange: setColumnsSize,
		onDensityChange: setComponentDensity,
		onColumnVisibilityChange: setComponentColumnVisibilityState,
		enableGlobalFilter: false,
		initialState: {
			density: disputesTableDensity,
			pagination: {
				pageIndex: componentPagination.pageIndex,
				pageSize: componentPagination.pageSize
			}},
		state: {
			sorting,
			isLoading,
			columnSizing: disputesTableColumnSizing,
			density,
			columnVisibility: componentColumnVisibilityState,
			pagination: {
				pageIndex: componentPagination.pageIndex,
				pageSize: componentPagination.pageSize
			},
		},
		rowCount: totalItemCount,
		displayColumnDefOptions: {
			'mrt-row-actions': {
				size: actionColumnsSize,
				muiTableHeadCellProps: {
					align: actionColumnAlign,
				} as any
			},
		},
		renderTopToolbarCustomActions: (): JSX.Element => toolbarCustomAction && toolbarCustomAction,
		enableRowActions,
		muiExpandButtonProps: ({ row, table }) => ({
			sx: {
				transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
				transition: 'transform 0.2s',
			},
		}),
	};

	if (rowActionMenuItems) {
		// @ts-ignore
		options.renderRowActionMenuItems = ({ row, closeMenu }): JSX.Element => rowActionMenuItems(row, closeMenu);
	}

	if (rowActions) {
		// @ts-ignore
		options.renderRowActions = ({ row }): JSX.Element => rowActions(row);
	}

	if (rowSelection) {
		// @ts-ignore
		options.enableRowSelection = enableRowSelectionCondition
			? (row: any): void => enableRowSelectionCondition(row)
			: enableRowSelection;
		// @ts-ignore
		options.enableSelectAll = enableSelectAll;
		// @ts-ignore
		options.enableMultiRowSelection = enableMultiRowSelection; //use radio buttons or checkboxes
		// @ts-ignore
		options.positionToolbarAlertBanner = 'none';
		// @ts-ignore
		options.getRowId = (row): void => row?.id || row?.transactionId; //give each row a more useful id
		// @ts-ignore
		options.onRowSelectionChange = (row): void => setRowSelection(row); //connect internal row selection state to your own

		if (positionToolbarAlertBanner) {
			// @ts-ignore
			options.positionToolbarAlertBanner = positionToolbarAlertBanner;
		}
		if (enableRowClicking) {
			// @ts-ignore
			options.muiTableBodyRowProps = ({ row }): any => ({
				//add onClick to row to select upon clicking anywhere in the row
				onClick: row.getToggleSelectedHandler(),
				sx: { cursor: 'pointer' },
			});
		}
		// @ts-ignore
		// @ts-ignore
		options.state = {
			...options.state,
		// @ts-ignore
			rowSelection,
		};
	}

	if (renderDetailPanel) {
		// @ts-ignore
		options.renderDetailPanel = renderDetailPanel;
	}

	const additionalTableClass = (): string => {
		switch (density) {
			case 'compact': return 'fc-table-compact';
			case 'comfortable': return 'fc-table-comfortable';
			case 'spacious': return 'fc-table-spacious';
		}
	};

	return (
		<div className={additionalTableClass()} data-cy={`table-${name}`}>
			<MaterialReactTable
				{...options}
				positionActionsColumn={positionActionsColumn}
			/>
		</div>
	);
};
